import { useLocation, useRouterState } from "@tanstack/react-router";
import { DateTime } from "luxon";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import { Spinner } from "./ui/spinner";

type LoadingScreenProps = {
  minVisibleMs?: number;
};

export const LoadingScreen = ({
  minVisibleMs = 200,
}: LoadingScreenProps = {}) => {
  const { t } = useTranslation();
  const routerState = useRouterState();
  const location = useLocation();
  const targetPathRef = useRef<string | null>(null);

  useEffect(() => {
    if (routerState.status === "pending") {
      const targetPath = routerState.location.pathname ?? null;
      if (targetPath !== location.pathname) {
        targetPathRef.current = targetPath;
        showGlobalLoader(t("loading"));
      }
      return;
    }
    if (
      targetPathRef.current == null ||
      location.pathname === targetPathRef.current
    ) {
      hideGlobalLoader(minVisibleMs);
      targetPathRef.current = null;
    }
  }, [
    routerState.status,
    routerState.location,
    location.pathname,
    t,
    minVisibleMs,
  ]);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      // Skip if the click originated inside an element that opted out (e.g. export button)
      if (target.closest("[data-no-loader]")) return;

      const el = target.closest("a[href], [data-navigate]");
      if (!el) return;

      // Skip loading screen for the link/navigate element itself
      if (el.hasAttribute("data-no-loader")) return;

      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
        return;

      if (el instanceof HTMLAnchorElement && el.target === "_blank") return;

      const currentPathname = location.pathname;

      if (el instanceof HTMLAnchorElement) {
        const rawHref = el.getAttribute("href") || "";
        if (rawHref.startsWith("blob:") || el.hasAttribute("download")) return;
        const targetPathname = rawHref.startsWith("#")
          ? rawHref.slice(1).split("?")[0].split("#")[0]
          : (() => {
              try {
                const url = new URL(rawHref, window.location.origin);
                return url.pathname;
              } catch {
                return rawHref.split("?")[0].split("#")[0];
              }
            })();
        if (
          targetPathname === "" ||
          targetPathname === "#" ||
          targetPathname === currentPathname
        )
          return;
        showGlobalLoader(t("loading"));
        return;
      }

      const dataNavigate = el.getAttribute("data-navigate");
      if (!dataNavigate) return;
      const targetPathname = dataNavigate.startsWith("#")
        ? dataNavigate.slice(1).split("?")[0].split("#")[0]
        : dataNavigate.split("?")[0].split("#")[0];
      if (targetPathname === currentPathname) return;
      showGlobalLoader(t("loading"));
    };

    document.addEventListener("click", handleClick, true);
    return () => document.removeEventListener("click", handleClick, true);
  }, [location.pathname, t]);

  useEffect(() => {
    if (
      targetPathRef.current == null ||
      location.pathname === targetPathRef.current
    ) {
      hideGlobalLoader(minVisibleMs);
      targetPathRef.current = null;
    }
  }, [location.pathname, minVisibleMs]);

  // Removed redundant idle-based hide; hiding is handled when the path matches target

  return null;
};

const showGlobalLoader = (label: string) => {
  const existingLoader = document.getElementById("global-loader");
  if (existingLoader) return;

  const loader = document.createElement("div");
  loader.id = "global-loader";
  loader.innerHTML = `
        <div id="global-loader-overlay" style="
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            z-index: 999999;
            background-color: color-mix(in oklch, var(--background) 60%, transparent);
            backdrop-filter: blur(6px);
            display: flex;
            align-items: center;
            justify-content: center;
            opacity: 0;
            transition: opacity 180ms ease;
        ">
            <div style="display:flex;flex-direction:column;align-items:center;gap:14px">
                <div class="cssloader">
                    <div class="triangle1"></div>
                    <div class="triangle2"></div>
                </div>
                <p class="loader-text">${label}</p>
            </div>
        </div>
        <style>
            .triangle1 { width: 0; height: 0; border-style: solid; border-width: 50px 50px 0 0; border-color: var(--primary) transparent transparent transparent; margin: 0 auto; animation: shk1 1s ease-in-out infinite normal; }
            .triangle2 { width: 0; height: 0; border-style: solid; border-width: 0 0 50px 50px; border-color: transparent transparent color-mix(in oklch, var(--accent) 60%, transparent) transparent; margin: -50px auto 0; animation: shk2 1s ease-in-out infinite alternate; }
            @keyframes shk1 { 0% { transform: rotate(-360deg); } 100% { } }
            @keyframes shk2 { 0% { transform: rotate(360deg); } 100% { } }
            @keyframes loaderTextPulse { 0%, 100% { opacity: 0.6; } 50% { opacity: 1; } }
            .loader-text { color: var(--foreground); margin: 30px auto 0; text-align: center; font-weight: 400; letter-spacing: normal; font-size: 14px; animation: loaderTextPulse 1.6s ease-in-out infinite; }
        </style>
    `;

  document.body.appendChild(loader);
  const overlay = document.getElementById("global-loader-overlay");
  if (overlay) {
    requestAnimationFrame(() => {
      overlay.style.opacity = "1";
    });
  }
  loader.setAttribute("data-shown-at", String(DateTime.now().toMillis()));
};

const hideGlobalLoader = (minVisibleMs: number) => {
  const loader = document.getElementById("global-loader");
  if (!loader) return;
  const overlay = document.getElementById("global-loader-overlay");
  const shownAtAttr = loader.getAttribute("data-shown-at");
  const shownAt = shownAtAttr ? parseInt(shownAtAttr, 10) : null;
  const now = DateTime.now().toMillis();
  const elapsed = shownAt != null ? now - shownAt : minVisibleMs;
  const remaining = elapsed < minVisibleMs ? minVisibleMs - elapsed : 0;

  const remove = () => loader.remove();

  if (overlay) {
    overlay.style.opacity = "0";
    window.setTimeout(remove, remaining + 50);
    return;
  }
  window.setTimeout(remove, remaining);
};

export const SuspenseLoadingScreen = () => {
  const { t } = useTranslation();

  return (
    <div className="fixed inset-0 z-[1200] flex flex-col justify-center items-center bg-background">
      <div className="flex flex-col items-center gap-4">
        <Spinner size="lg" />
        <p className="text-foreground text-base font-medium">{t("loading")}</p>
      </div>
    </div>
  );
};
